import type { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxMaybeArray } from '@onyx/angular';
import { OnyxLanguagePipe } from '@onyx/angular';
import { castArray } from 'lodash';
import type z from 'zod';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { FleetTypeHelper } from '../helpers/fleet-type.helper';
import { ExportHelper } from './../../../common/helpers/export.helper';
import type { Fleet } from './../interfaces/fleet';
import type { getFleetXlsxSchema } from './fleet-xlsx-schema';

export type FleetExportData = z.input<ReturnType<typeof getFleetXlsxSchema>>;

export const getFleetExportData = (
  data: OnyxMaybeArray<Fleet>,
  injector: Injector,
): FleetExportData[] => {
  const translateService = injector.get(TranslateService);
  const languagePipe = injector.get(OnyxLanguagePipe);

  return castArray(data).map((fleet) => {
    const {
      generalInformation,
      vehicleMeasurements,
      cargoSpace,
      fuelTankCapacity,
      ownership,
      licensesAndPermits,
      allowedCountries,
      note,
      additionalParameters,
      tachograph,
      registrationCertificate,
      co2Emissions,
      thirdPartyLiabilityInsurance,
      comprehensiveInsurance,
    } = fleet;

    return {
      /* generalInformation */
      category: ExportHelper.mapField(
        generalInformation.category,
        FleetTypeHelper.isVehicle(fleet)
          ? DictionaryCode.VEHICLE_CATEGORY
          : DictionaryCode.TRAILER_CATEGORY,
      ),
      type: ExportHelper.mapField(
        generalInformation.type,
        DictionaryCode.VEHICLE_TYPE,
      ),
      registrationNumber: generalInformation.registrationNumber,
      sideNumber: generalInformation.sideNumber,
      vinNumber: generalInformation.vinNumber,
      make: generalInformation.make,
      model: generalInformation.model,
      manufactureYear: generalInformation.manufactureYear,
      firstRegistrationDate: generalInformation.firstRegistrationDate,
      registrationCountry: ExportHelper.mapField(
        generalInformation.registrationCountry,
        DictionaryCode.COUNTRY,
      ),
      state: ExportHelper.mapField(
        generalInformation.state,
        DictionaryCode.FLEET_STATE,
      ),
      planningMode: ExportHelper.mapField(
        generalInformation.planningMode,
        DictionaryCode.VEHICLE_PLANNING_MODE,
      ),
      initialMileage: generalInformation.initialMileage,
      maxSpeed: generalInformation.maxSpeed,
      assignedBase: generalInformation.assignedBase?.name ?? null,

      /* vehicleMeasurements */
      vehicleCurbWeight: vehicleMeasurements.curbWeight,
      vehicleAxleCount: vehicleMeasurements.axleCount,
      vehicleLength: vehicleMeasurements.length,
      vehicleWidth: vehicleMeasurements.width,
      vehicleHeight: vehicleMeasurements.height,

      /* cargoSpace */
      loadCapacity: cargoSpace.loadCapacity,
      volumeCapacity: cargoSpace.volumeCapacity,
      chambersNumber: cargoSpace.chambersNumber,

      tankCertificates: ExportHelper.mapField(
        cargoSpace.tankCertificates,
        DictionaryCode.TANK_CERTIFICATE,
        translateService,
      ),
      dischargeTypes: ExportHelper.mapField(
        cargoSpace.dischargeTypes,
        DictionaryCode.DISCHARGE_TYPE,
        translateService,
      ),
      europalletsNumber: cargoSpace.europalletsNumber,
      cargoSpaceLength: cargoSpace.dimensions.length,
      cargoSpaceWidth: cargoSpace.dimensions.width,
      cargoSpaceHeight: cargoSpace.dimensions.height,

      /* fuelTankCapacity */
      mainTank: fuelTankCapacity?.mainTank ?? null,
      fuelType: ExportHelper.mapField(
        fuelTankCapacity?.fuelType,
        DictionaryCode.FUEL_TYPE,
      ),
      additionalTank: fuelTankCapacity?.additionalTank ?? null,
      adBlueTank: fuelTankCapacity?.adBlueTank ?? null,
      generatorFuelTank: fuelTankCapacity?.generatorFuelTank ?? null,

      /* ownership */
      isForSale: ownership.isForSale,
      ownershipType: ExportHelper.mapField(
        ownership.type,
        DictionaryCode.OWNERSHIP_TYPE,
      ),
      vehicleValue: ownership.vehicleValue?.value ?? null,
      vehicleValueCurrency: ExportHelper.mapField(
        ownership.vehicleValue?.currency,
        DictionaryCode.CURRENCY,
      ),
      depreciationPeriodFrom: ownership.depreciationPeriod?.from ?? null,
      depreciationPeriodTo: ownership.depreciationPeriod?.to ?? null,
      selfDeposit: ownership.selfDeposit?.value ?? null,
      selfDepositCurrency: ExportHelper.mapField(
        ownership.selfDeposit?.currency,
        DictionaryCode.CURRENCY,
      ),
      buyout: ownership.buyout?.value ?? null,
      buyoutCurrency: ExportHelper.mapField(
        ownership.buyout?.currency,
        DictionaryCode.CURRENCY,
      ),
      installment: ownership.installment?.value ?? null,
      installmentCurrency: ExportHelper.mapField(
        ownership.installment?.currency,
        DictionaryCode.CURRENCY,
      ),
      firstInstallmentDate: ownership.firstAndLastInstallmentDate?.from ?? null,
      lastInstallmentDate: ownership.firstAndLastInstallmentDate?.to ?? null,
      margin: ownership.margin,
      interestRateType: ExportHelper.mapField(
        ownership.interestRateType,
        DictionaryCode.INTEREST_RATE_TYPE,
      ),

      /* licensesAndPermits */
      issuingCountry: ExportHelper.mapField(
        licensesAndPermits?.issuingCountry,
        DictionaryCode.COUNTRY,
      ),
      communityLicenseAbstractNumber:
        licensesAndPermits?.communityLicenseAbstractNumber,
      permitAbstractNumber: licensesAndPermits?.permitAbstractNumber,

      /* countriesWhitelist */
      countriesGroup: ExportHelper.mapField(
        allowedCountries?.group,
        DictionaryCode.COUNTRIES_GROUP,
      ),
      countriesWhitelist: !allowedCountries?.group
        ? ExportHelper.mapField(
            allowedCountries?.whitelist,
            DictionaryCode.COUNTRY,
            translateService,
          )
        : null,

      /* note */
      note: note?.content ?? null,

      /* additionalParameters */
      isLowDeck: additionalParameters.isLowDeck,
      semiTrailerSize: ExportHelper.mapField(
        additionalParameters.semiTrailerSize,
        DictionaryCode.SEMI_TRAILER_SIZE,
      ),
      trailerSize: ExportHelper.mapField(
        additionalParameters.trailerSize,
        DictionaryCode.TRAILER_SIZE,
      ),
      coilWellLength: additionalParameters.coilWellLength,
      adrClasses: ExportHelper.mapField(
        additionalParameters.adrClasses,
        DictionaryCode.ADR_CLASS,
        translateService,
      ),
      hasHaccp: additionalParameters.hasHaccp,
      hooksNumber: additionalParameters.hooksNumber,
      hasHdsCrane: additionalParameters.hasHdsCrane,
      hasCustomsSecuringRope: additionalParameters.hasCustomsSecuringRope,
      matsNumber: additionalParameters.matsNumber,
      hasLoadingsRamps: additionalParameters.hasLoadingsRamps,
      beltsNumber: additionalParameters.beltsNumber,
      hasSanitaryInspection: additionalParameters.hasSanitaryInspection,
      temperatureRangeFrom: additionalParameters.temperatureRange?.from ?? null,
      temperatureRangeTo: additionalParameters.temperatureRange?.to ?? null,
      hasTailLift: additionalParameters.hasTailLift,
      hasDumpContainer: additionalParameters.hasDumpContainer,
      hasForklift: additionalParameters.hasForklift,
      otherVehicleParameters:
        additionalParameters.otherVehicleParameters
          ?.map((parameter) => languagePipe.transform(parameter.names))
          .join(', ') ?? null,

      /* tachograph */
      tachographModel: ExportHelper.mapField(
        tachograph?.model,
        DictionaryCode.TACHOGRAPH_MODEL,
      ),
      tachographMake: ExportHelper.mapField(
        tachograph?.make,
        DictionaryCode.TACHOGRAPH_MAKE,
      ),
      tachographNextLegalizationDate: tachograph?.nextLegalizationDate ?? null,

      /* registrationCertificate */
      technicalInspectionExpiryDate:
        registrationCertificate.technicalInspectionExpiryDate,

      /* co2Emissions */
      emissionStandard: ExportHelper.mapField(
        co2Emissions?.emissionStandard,
        DictionaryCode.EMISSION_STANDARD,
      ),

      /* thirdPartyLiabilityInsurance */
      thirdPartyLiabilityInsuranceNumber:
        thirdPartyLiabilityInsurance.insuranceNumber,
      thirdPartyLiabilityExpiryDate: thirdPartyLiabilityInsurance.expiryDate,
      thirdPartyLiabilityAnnualCost:
        thirdPartyLiabilityInsurance.annualCost?.value,
      thirdPartyLiabilityAnnualCostCurrency: ExportHelper.mapField(
        thirdPartyLiabilityInsurance.annualCost?.currency,
        DictionaryCode.CURRENCY,
      ),
      thirdPartyLiabilityInsuranceSum:
        thirdPartyLiabilityInsurance.insuranceSum?.value ?? null,
      thirdPartyLiabilityInsuranceSumCurrency: ExportHelper.mapField(
        thirdPartyLiabilityInsurance.insuranceSum?.currency,
        DictionaryCode.CURRENCY,
      ),

      /* comprehensiveInsurance */
      comprehensiveInsuranceNumber: comprehensiveInsurance?.insuranceNumber,
      comprehensiveExpiryDate: comprehensiveInsurance?.expiryDate ?? null,
      comprehensiveAnnualCost:
        comprehensiveInsurance?.annualCost?.value ?? null,
      comprehensiveAnnualCostCurrency: ExportHelper.mapField(
        comprehensiveInsurance?.annualCost?.currency,
        DictionaryCode.CURRENCY,
      ),
      comprehensiveInsuranceSum:
        comprehensiveInsurance?.insuranceSum?.value ?? null,
      comprehensiveInsuranceSumCurrency: ExportHelper.mapField(
        comprehensiveInsurance?.insuranceSum?.currency,
        DictionaryCode.CURRENCY,
      ),
    } satisfies FleetExportData;
  });
};
